import React, { useState } from "react";
import { Store, Check, Truck, Tag, Headphones, FileText, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { SectionHeading } from "../components/Section";
import { submitWholesale } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { toast } from "sonner";

export default function Wholesale() {
  const { t } = useLang();
  const [form, setForm] = useState({ company: "", name: "", email: "", country: "", phone: "", volume: "", interest: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitWholesale(form); setSent(true); toast.success(t("wholesale.success")); }
    catch { toast.error(t("common.error")); } finally { setLoading(false); }
  };

  const benefits = [[Tag, "b1"], [Truck, "b2"], [Headphones, "b3"], [FileText, "b4"]];

  return (
    <div className="pt-24 pb-16 container-lumeo">
      <SectionHeading eyebrow={t("footer.reseller")} title={t("wholesale.title")} subtitle={t("wholesale.subtitle")} />
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="rounded-2xl card-ceramic p-6">
            <div className="flex items-center gap-3"><Store className="h-6 w-6 text-cyanx" /><h3 className="font-display text-xl">{t("wholesale.dropTitle")}</h3></div>
            <p className="mt-3 text-muted-foreground text-sm">{t("wholesale.dropBody")}</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {benefits.map(([Icon, k]) => (
                <div key={k} className="flex items-center gap-3 rounded-xl hairline p-3"><Icon className="h-5 w-5 text-cyanx shrink-0" /><span className="text-sm">{t(`wholesale.${k}`)}</span></div>
              ))}
            </div>
          </div>
        </div>

        {sent ? (
          <div className="rounded-2xl card-ceramic p-8 flex flex-col items-center justify-center text-center">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-mintx/15 shadow-glow-mint"><Check className="h-8 w-8 text-mintx" /></span>
            <p className="mt-4 text-foreground/85">{t("wholesale.success")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl card-ceramic p-6 space-y-4" data-testid="wholesale-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("wholesale.company")} required><Input required value={form.company} onChange={set("company")} className="bg-[#0b0d12] border-border" data-testid="wholesale-company" /></Field>
              <Field label={t("wholesale.name")} required><Input required value={form.name} onChange={set("name")} className="bg-[#0b0d12] border-border" /></Field>
              <Field label={t("wholesale.email")} required><Input required type="email" value={form.email} onChange={set("email")} className="bg-[#0b0d12] border-border" data-testid="wholesale-email" /></Field>
              <Field label={t("wholesale.country")}><Input value={form.country} onChange={set("country")} className="bg-[#0b0d12] border-border" /></Field>
              <Field label={t("wholesale.phone")}><Input value={form.phone} onChange={set("phone")} className="bg-[#0b0d12] border-border" /></Field>
              <Field label={t("wholesale.volume")}><Input value={form.volume} onChange={set("volume")} placeholder="50\u2013100" className="bg-[#0b0d12] border-border" /></Field>
            </div>
            <Field label={t("wholesale.message")}><Textarea value={form.message} onChange={set("message")} rows={4} className="bg-[#0b0d12] border-border resize-none" /></Field>
            <Button type="submit" disabled={loading} data-testid="wholesale-submit" className="w-full btn-glow border-0 h-12">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("wholesale.submit")}</Button>
          </form>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">{label}{required && <span className="text-cyanx"> *</span>}</Label>{children}</div>
);
